import React, { useState, useEffect } from "react";
import { Box, Typography, Card, CardContent, Button } from "@mui/material";
import { useParams } from "react-router-dom";
import { db, auth } from "../../firebase";
import { doc, getDoc, setDoc, Timestamp, } from "firebase/firestore";
import Swal from "sweetalert2";

import StudentInfo from "./tabs/StudentInfo";
import FatherInfo from "./tabs/FatherInfo";
import MotherInfo from "./tabs/MotherInfo";
import GuardianInfo from "./tabs/GuardianInfo";
import MedicalRecord from "./tabs/MedicalRecord";
import Declaration from "./tabs/Declaration";

function StudentProfile() {
  const { studentId } = useParams();

  const [form, setForm] = useState({});
  const [schoolId, setSchoolId] = useState("");

  // ---------------- FETCH SCHOOL ID ----------------
  useEffect(() => {
    const fetchSchoolId = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", auth.currentUser?.uid));
        if (userDoc.exists()) {
          setSchoolId(userDoc.data().schoolId);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchSchoolId();
  }, []);

  // ---------------- FETCH STUDENT / ENQUIRY DATA ----------------
  useEffect(() => {
    const loadStudent = async () => {
      if (!studentId) return;

      try {
        const studentRef = doc(db, "students", studentId);
        const studentSnap = await getDoc(studentRef);

        if (studentSnap.exists()) {
          const studentData = studentSnap.data();
          const admissionSnap = await getDoc(doc(db, "admission", studentId));
          const admissionData = admissionSnap.exists() ? admissionSnap.data() : {};

          setForm({
            id: studentId,
            ...studentData,
            ...admissionData
          });
        } else {
          const enquiryRef = doc(db, "admissionEnquiries", studentId);
          const enquirySnap = await getDoc(enquiryRef);

          if (!enquirySnap.exists()) {
            Swal.fire("Error", "Student not found", "error");
            return;
          }

          const data = enquirySnap.data();
          const parts = data.studentName?.split(" ") || [];
          setForm({
            id: studentId,
            firstname: parts[0] || "",
            surname: parts.slice(1).join(" ") || "",
            fathername: data.visitorsName || "",
            mothername: "",
            gender: "",
            dob: data.dob || "",
            placeofbirth: data.birthPlace || "",
            religion: "",
            nationality: "Indian",
            caste: "",
            class: "",
            rollno: "",
            subcaste: "",
            adhar: "",
            portal: "",
            language: "",
            lastschool: "",
            schoolId: data.schoolId || ""
          });
        }
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to load student data", "error");
      }
    };

    loadStudent();
  }, [studentId]);

  // ---------------- HANDLE CHANGE ----------------
  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ---------------- SAVE DATA ----------------
  const saveData = async () => {
    if (!studentId || !schoolId) {
      Swal.fire("Error", "Missing student or school ID", "error");
      return;
    }

    try {
      const studentData = {
        rollno: form.rollno || "",
        title: form.title || "",
        class: form.class || "",
        firstname: form.firstname || "",
        surname: form.surname || "",
        fathername: form.fathername || "",
        mothername: form.mothername || "",
        gender: form.gender || "",
        dob: form.dob || "",
        placeofbirth: form.placeofbirth || "",
        father_photo: form.father_photo || "",
        mother_photo: form.mother_photo || "",
        religion: form.religion || "",
        nationality: form.nationality || "",
        caste: form.caste || "",
        subcaste: form.subcaste || "",
        adhar: form.adhar || "",
        portal: form.portal || "",
        language: form.language || "",
        lastschool: form.lastschool || "",
        schoolId,
        updatedAt: Timestamp.now()
      };

      await setDoc(doc(db, "students", studentId), studentData, { merge: true });

      const admissionData = {
        ...form,
        rollno: form.rollno || "",
        class: form.class || "",
        studentId,
        schoolId,
        updatedAt: Timestamp.now(),
        createdAt: form.createdAt || Timestamp.now(),
        status: form.status || "Active",
        userid: auth.currentUser?.uid
      };

      [
        "firstname", "surname", "gender", "dob",
        "placeofbirth", "religion", "nationality",
        "caste", "subcaste", "adhar", "portal",
        "language", "lastschool"
      ].forEach(field => delete admissionData[field]);

      await setDoc(doc(db, "admission", studentId), admissionData, { merge: true });

      Swal.fire("Success", "Student data saved successfully", "success");

    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  return (
    <Box p={2} bgcolor="#f5f6fa" minHeight="100vh">
      <Typography variant="h6" fontWeight={700} mb={2}>
        Student Profile
      </Typography>

      <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <StudentInfo form={form} handleChange={handleChange} studentId={studentId} schoolId={schoolId} />
      </Card>

      <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <FatherInfo form={form} handleChange={handleChange} studentId={studentId} schoolId={schoolId} />
      </Card>

      <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <MotherInfo form={form} handleChange={handleChange} studentId={studentId} schoolId={schoolId} />
      </Card>

      <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <GuardianInfo form={form} handleChange={handleChange} studentId={studentId} schoolId={schoolId} />
      </Card>

      <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <MedicalRecord form={form} handleChange={handleChange} studentId={studentId} schoolId={schoolId} />
      </Card>

      <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mb: 3 }}>
        <Declaration form={form} handleChange={handleChange} studentId={studentId} schoolId={schoolId} />
      </Card>

      {/* <Box mt={3} textAlign="center">
        <Button variant="contained" color="secondary" onClick={saveData}>
          Save All
        </Button>
      </Box> */}
    </Box>
  );
}

export default StudentProfile;